import { BaseService } from "../../core/baseService";
import {
  ServiceOptionGroupDto,
  CreateServiceOptionGroupDto,
  UpdateServiceOptionGroupDto,
  CreateServiceOptionDto,
  UpdateServiceOptionDto,
  AssignOptionGroupsDto,
  ServiceOptionDto,
} from "../types/serviceOption.types";
import { ApiResult } from "../../core/types";

export interface ServiceOptionGroupServiceInterface {
  // Option Groups
  getAll(includeInactive?: boolean): Promise<ApiResult<ServiceOptionGroupDto[]>>;
  getById(id: string): Promise<ApiResult<ServiceOptionGroupDto>>;
  getByServiceType(serviceTypeId: string): Promise<ApiResult<ServiceOptionGroupDto[]>>;
  create(data: CreateServiceOptionGroupDto): Promise<ApiResult<ServiceOptionGroupDto>>;
  update(id: string, data: UpdateServiceOptionGroupDto): Promise<ApiResult<void>>;
  delete(id: string): Promise<ApiResult<void>>;
  toggleActive(id: string, isActive: boolean): Promise<ApiResult<void>>;

  // Options
  addOption(optionGroupId: string, data: CreateServiceOptionDto): Promise<ApiResult<ServiceOptionDto>>;
  updateOption(optionId: string, data: UpdateServiceOptionDto): Promise<ApiResult<void>>;
  deleteOption(optionId: string): Promise<ApiResult<void>>;

  // Assignment
  assignToServiceType(serviceTypeId: string, data: AssignOptionGroupsDto): Promise<ApiResult<void>>;
}

export class ServiceOptionGroupService extends BaseService implements ServiceOptionGroupServiceInterface {
  // Option Groups
  async getAll(includeInactive = false): Promise<ApiResult<ServiceOptionGroupDto[]>> {
    return this.handleRequest<ServiceOptionGroupDto[]>(
      this.client.get("/api/admin/service-option-groups", {
        params: { includeInactive },
      })
    );
  }

  async getById(id: string): Promise<ApiResult<ServiceOptionGroupDto>> {
    return this.handleRequest<ServiceOptionGroupDto>(
      this.client.get(`/api/admin/service-option-groups/${id}`)
    );
  }

  async getByServiceType(serviceTypeId: string): Promise<ApiResult<ServiceOptionGroupDto[]>> {
    return this.handleRequest<ServiceOptionGroupDto[]>(
      this.client.get(`/api/admin/service-option-groups/by-service-type/${serviceTypeId}`)
    );
  }

  async create(data: CreateServiceOptionGroupDto): Promise<ApiResult<ServiceOptionGroupDto>> {
    return this.handleRequest<ServiceOptionGroupDto>(
      this.client.post("/api/admin/service-option-groups", data)
    );
  }

  async update(id: string, data: UpdateServiceOptionGroupDto): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/service-option-groups/${id}`, data)
    );
  }

  async delete(id: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/service-option-groups/${id}`)
    );
  }

  async toggleActive(id: string, isActive: boolean): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.patch(`/api/admin/service-option-groups/${id}/toggle`, null, {
        params: { isActive },
      })
    );
  }

  // Options
  async addOption(optionGroupId: string, data: CreateServiceOptionDto): Promise<ApiResult<ServiceOptionDto>> {
    return this.handleRequest<ServiceOptionDto>(
      this.client.post(`/api/admin/service-option-groups/${optionGroupId}/options`, data)
    );
  }

  async updateOption(optionId: string, data: UpdateServiceOptionDto): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/service-option-groups/options/${optionId}`, data)
    );
  }

  async deleteOption(optionId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/service-option-groups/options/${optionId}`)
    );
  }

  // Assignment
  async assignToServiceType(serviceTypeId: string, data: AssignOptionGroupsDto): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post(`/api/admin/service-option-groups/assign/${serviceTypeId}`, data)
    );
  }
}
